import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/shadcn/form'
import { Input } from '@/components/shadcn/input'
import { Button } from '@/components/shadcn/button'
import { useRestClient } from '@/contexts/QueryProvider'
import { useAppContext } from '@/catalog-apps/hooks/useAppContext'
import { Alert, AlertDescription, AlertTitle } from '@/components/shadcn/alert'
import { IconWarningOutline } from '@icons/IconWarningOutline'
import { toast } from 'sonner'
import { useSessionStorage } from '@/hooks'

const formSchema = z.object({
  threadDescription: z.string().min(1, 'Thread description is required'),
})

export function CreateThreadForm({
  closeForm,
  channelId,
  threadKey,
  channelKey,
}: {
  closeForm: () => void
  channelId?: number
  threadKey: string
  channelKey: string
}) {
  const { appId, workspaceId } = useAppContext()
  const [currentUserId] = useSessionStorage('sub', 0)

  const { putCollaborationApiCreateCollaborationThread, getCollaborationApiGetChannelThreadDetailsByKey } =
    useRestClient()

  const { mutate: createThread } = putCollaborationApiCreateCollaborationThread.useMutation()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      threadDescription: '',
    },
  })

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    if (!appId || !workspaceId || !channelId) {
      toast.error('App ID, workspace ID, and channel ID are required to create a new thread.')
      return
    }
    createThread(
      {
        path: { applicationId: appId, workspaceId, channelId },
        body: {
          collaborationChannelId: channelId,
          workspaceId,
          key: threadKey,
          description: data.threadDescription,
          value: '',
          assignedTo: currentUserId,
        },
      },
      {
        onSuccess: () => {
          toast.success('Thread created successfully.')
          getCollaborationApiGetChannelThreadDetailsByKey.invalidateQueries({
            parameters: {
              path: { applicationId: appId, workspaceId, channelKey, threadKey },
            },
          })
          closeForm()
        },
        onError: () => {
          toast.error('Failed to create thread.')
        },
      }
    )
  }

  if (channelId === undefined) {
    return (
      <Alert variant="destructive">
        <IconWarningOutline />
        <AlertTitle>Channel Id is required.</AlertTitle>
        <AlertDescription>A channel ID is required to create a new thread.</AlertDescription>
      </Alert>
    )
  }

  return (
    <Form {...form}>
      <form className="w-full animate-fade-in-delayed space-y-5" onSubmit={form.handleSubmit(onSubmit)}>
        <FormField
          control={form.control}
          name="threadDescription"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="sr-only">Thread Description</FormLabel>
              <FormDescription className="sr-only text-xs">This is the description of the thread.</FormDescription>
              <FormControl>
                <Input
                  {...field}
                  placeholder="New thread description..."
                  autoFocus
                  className="rounded-l-full rounded-r-full"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={closeForm}>
            Cancel
          </Button>
          <Button type="submit">Create</Button>
        </div>
      </form>
    </Form>
  )
}
